// UPost.com - Kindle Research Pro Dashboard with A/B Testing
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { 
  Search, BookOpen, TrendingUp, DollarSign, Target, Lightbulb, 
  FileText, Palette, Plus, Trash2, Calculator, ChevronRight,
  Lock, CheckCircle, Loader2, AlertCircle, Sparkles, Clock, Zap, CreditCard
} from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import KindleVariantA from '../components/kindle/KindleVariantA';
import KindleVariantB from '../components/kindle/KindleVariantB';
import KindleVariantC from '../components/kindle/KindleVariantC';
import KindleVariantDExpired from '../components/kindle/KindleVariantDExpired';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function KindleResearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [abVariant, setAbVariant] = useState(null);
  const [niches, setNiches] = useState([]);
  const [selectedNiche, setSelectedNiche] = useState(null);
  const [books, setBooks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [outlines, setOutlines] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Modal states
  const [showNicheModal, setShowNicheModal] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showBsrCalculator, setShowBsrCalculator] = useState(false);
  const [showIdeaGenerator, setShowIdeaGenerator] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  
  // Form states
  const [nicheForm, setNicheForm] = useState({
    name: '', category: '', subcategory: '', description: '', competition_level: 'medium'
  });
  const [bookForm, setBookForm] = useState({
    title: '', author: '', asin: '', bsr: '', category: '', price: '',
    review_count: '', rating: '', tropes: '', keywords: '', cover_style: '', notes: ''
  });
  const [bsrInput, setBsrInput] = useState('');
  const [bsrResult, setBsrResult] = useState(null);
  const [bsrRemaining, setBsrRemaining] = useState(null);
  
  // AI generation states
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [generatingOutline, setGeneratingOutline] = useState(false);
  const [generatedIdeas, setGeneratedIdeas] = useState(null);
  const [generatedOutline, setGeneratedOutline] = useState(null);
  const [ideaRequest, setIdeaRequest] = useState({
    niche_id: '', style_preference: '', target_audience: '', tropes_to_include: []
  });
  
  // Categories for reference
  const [categories, setCategories] = useState({});
  
  // Processing states
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [paypalClientId, setPaypalClientId] = useState(null);

  // Get visitor ID from localStorage
  const getVisitorId = useCallback(() => {
    let visitorId = localStorage.getItem('kindle_ab_visitor');
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem('kindle_ab_visitor', visitorId);
    }
    return visitorId;
  }, []);

  useEffect(() => {
    initializeAndLoad();
    handlePaymentReturn();
  }, []);

  const initializeAndLoad = async () => {
    try {
      // Fetch PayPal client ID
      try {
        const ppRes = await fetch(`${API_URL}/api/paypal/client-id`);
        const ppData = await ppRes.json();
        if (ppData.client_id) setPaypalClientId(ppData.client_id);
      } catch (e) { /* PayPal optional */ }
      
      // First check subscription status (for paid users)
      const status = await api.request('/kindle-research/status');
      setSubscriptionStatus(status);
      
      if (status.has_access) {
        // Paid user - full access
        await loadAllData();
        setLoading(false);
        return;
      }
      
      // Not a paid user - check A/B variant
      const visitorId = getVisitorId();
      const variantResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kindle-ab-test/variant?visitor_id=${visitorId}`,
        { credentials: 'include' }
      );
      const variant = await variantResponse.json();
      setAbVariant(variant);
      
      // Load data based on variant access
      if (variant.access?.save_niches || variant.access?.categories_reference) {
        await loadCategoriesOnly();
      }
      
      // For variant C and D, load user's saved data if they have any
      if (variant.access?.save_niches) {
        try {
          const nichesRes = await api.request('/kindle-research/niches');
          setNiches(nichesRes.niches || []);
        } catch (e) {
          // May fail if not authenticated, that's ok
        }
      }
      
    } catch (err) {
      console.error('Failed to initialize:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesOnly = async () => {
    try {
      // Categories are available without full auth for variants B, C, D
      const categoriesRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kindle-research/categories`,
        { 
          headers: user ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {},
          credentials: 'include'
        }
      );
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.categories || {});
      }
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const handlePaymentReturn = async () => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const type = searchParams.get('type');
    const method = searchParams.get('method');
    
    if (payment === 'success' && method === 'paypal') {
      setSuccessMessage('PayPal payment successful! Your access is being activated.');
      initializeAndLoad();
      window.history.replaceState({}, '', '/kindle-research');
    } else if (payment === 'success' && sessionId) {
      setSuccessMessage('Processing payment...');
      try {
        await api.request(`/kindle-research/payment/status/${sessionId}`);
        
        // Track conversion
        const visitorId = getVisitorId();
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kindle-ab-test/track?visitor_id=${visitorId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_type: type === 'setup' ? 'setup_paid' : 'subscription_paid',
            metadata: { session_id: sessionId }
          }),
          credentials: 'include'
        });
        
        setSuccessMessage(type === 'setup' 
          ? '🎉 Setup fee paid! Now subscribe to activate full access.'
          : '🎉 Subscription activated! Welcome to Kindle Research Pro!');
        initializeAndLoad();
        window.history.replaceState({}, '', '/kindle-research');
      } catch (err) {
        setError('Payment verification failed. Please contact support.');
      }
    } else if (payment === 'cancelled') {
      setError('Payment was cancelled.');
      window.history.replaceState({}, '', '/kindle-research');
    }
  };

  const loadAllData = async () => {
    try {
      const [nichesRes, ideasRes, outlinesRes, categoriesRes] = await Promise.all([
        api.request('/kindle-research/niches'),
        api.request('/kindle-research/ideas'),
        api.request('/kindle-research/outlines'),
        api.request('/kindle-research/categories')
      ]);
      
      setNiches(nichesRes.niches || []);
      setIdeas(ideasRes.ideas || []);
      setOutlines(outlinesRes.outlines || []);
      setCategories(categoriesRes.categories || {});
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const checkFeatureAccess = async (feature) => {
    if (subscriptionStatus?.has_access) return { allowed: true };
    
    const visitorId = getVisitorId();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kindle-ab-test/check-limits?feature=${feature}&visitor_id=${visitorId}`,
        { credentials: 'include' }
      );
      return await response.json();
    } catch (e) {
      return { allowed: false, reason: 'error' };
    }
  };

  const trackFeatureUse = async (feature, metadata = {}) => {
    const visitorId = getVisitorId();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kindle-ab-test/track?visitor_id=${visitorId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: feature, metadata }),
        credentials: 'include'
      });
    } catch (e) {
      console.error('Failed to track:', e);
    }
  };

  const handlePurchaseSetup = async () => {
    setProcessing(true);
    setError('');
    try {
      const response = await api.request('/kindle-research/purchase/setup', {
        method: 'POST',
        body: JSON.stringify({ host_url: window.location.origin })
      });
      window.location.href = response.url;
    } catch (err) {
      setError('Failed to start checkout: ' + err.message);
      setProcessing(false);
    }
  };

  const handlePurchaseSubscription = async () => {
    setProcessing(true);
    setError('');
    try {
      const response = await api.request('/kindle-research/purchase/subscription', {
        method: 'POST',
        body: JSON.stringify({ host_url: window.location.origin })
      });
      window.location.href = response.url;
    } catch (err) {
      setError('Failed to start checkout: ' + err.message);
      setProcessing(false);
    }
  };

  const handleCreateNiche = async () => {
    // Check limits for variants C
    const access = await checkFeatureAccess('save_niche');
    if (!access.allowed) {
      setUpgradeMessage(access.message || 'Upgrade to create niches and save your research!');
      setShowUpgradeModal(true);
      setShowNicheModal(false);
      return;
    }
    
    // Check max niches for variant C
    if (access.max_niches && niches.length >= access.max_niches) {
      setUpgradeMessage(`You've reached the limit of ${access.max_niches} niche. Upgrade for unlimited niches!`);
      setShowUpgradeModal(true);
      setShowNicheModal(false);
      return;
    }
    
    setProcessing(true);
    try {
      await api.request('/kindle-research/niches', {
        method: 'POST',
        body: JSON.stringify(nicheForm)
      });
      setShowNicheModal(false);
      setNicheForm({ name: '', category: '', subcategory: '', description: '', competition_level: 'medium' });
      await loadAllData();
      setSuccessMessage('Niche created successfully!');
      trackFeatureUse('niche_created');
    } catch (err) {
      setError('Failed to create niche: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleAddBook = async () => {
    if (!selectedNiche) return;
    
    // Check limits
    const access = await checkFeatureAccess('save_book');
    if (!access.allowed) {
      setUpgradeMessage(access.message || 'Upgrade to save books and track competition!');
      setShowUpgradeModal(true);
      setShowBookModal(false);
      return;
    }
    
    // Check max books for variant C
    if (access.max_books_per_niche && books.length >= access.max_books_per_niche) {
      setUpgradeMessage(`You've reached the limit of ${access.max_books_per_niche} books per niche. Upgrade for unlimited tracking!`);
      setShowUpgradeModal(true);
      setShowBookModal(false);
      return;
    }
    
    setProcessing(true);
    try {
      const bookData = {
        ...bookForm,
        bsr: bookForm.bsr ? parseInt(bookForm.bsr) : null,
        price: bookForm.price ? parseFloat(bookForm.price) : null,
        review_count: bookForm.review_count ? parseInt(bookForm.review_count) : null,
        rating: bookForm.rating ? parseFloat(bookForm.rating) : null,
        tropes: bookForm.tropes ? bookForm.tropes.split(',').map(t => t.trim()) : [],
        keywords: bookForm.keywords ? bookForm.keywords.split(',').map(k => k.trim()) : []
      };
      
      await api.request(`/kindle-research/niches/${selectedNiche.id}/books`, {
        method: 'POST',
        body: JSON.stringify(bookData)
      });
      
      setShowBookModal(false);
      setBookForm({
        title: '', author: '', asin: '', bsr: '', category: '', price: '',
        review_count: '', rating: '', tropes: '', keywords: '', cover_style: '', notes: ''
      });
      await loadNicheBooks(selectedNiche.id);
      setSuccessMessage('Book added successfully!');
      trackFeatureUse('book_added');
    } catch (err) {
      setError('Failed to add book: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const loadNicheBooks = async (nicheId) => {
    try {
      const response = await api.request(`/kindle-research/niches/${nicheId}`);
      setSelectedNiche(response.niche);
      setBooks(response.books || []);
    } catch (err) {
      console.error('Failed to load niche:', err);
    }
  };

  const handleCalculateBsr = async () => {
    if (!bsrInput) return;
    
    // Check access
    const access = await checkFeatureAccess('bsr_calculator');
    if (!access.allowed) {
      if (access.reason === 'daily_limit_reached') {
        setUpgradeMessage(`${access.message}`);
      } else {
        setUpgradeMessage('BSR Calculator is available in our premium plans. Upgrade to estimate sales!');
      }
      setShowUpgradeModal(true);
      return;
    }
    
    setBsrRemaining(access.remaining);
    
    try {
      const response = await api.request(`/kindle-research/bsr-calculator/${bsrInput}`);
      setBsrResult(response);
      trackFeatureUse('bsr_calculation', { bsr: bsrInput });
      
      // Update remaining count
      if (access.remaining !== undefined) {
        setBsrRemaining(access.remaining - 1);
      }
    } catch (err) {
      setError('Failed to calculate: ' + err.message);
    }
  };

  const handleGenerateIdeas = async () => {
    if (!ideaRequest.niche_id) {
      setError('Please select a niche first');
      return;
    }
    
    // Check access
    const access = await checkFeatureAccess('ai_ideas');
    if (!access.allowed) {
      setUpgradeMessage(access.message || 'AI Book Idea Generator is a premium feature. Upgrade to generate winning book concepts!');
      setShowUpgradeModal(true);
      setShowIdeaGenerator(false);
      return;
    }
    
    setGeneratingIdeas(true);
    setError('');
    try {
      const response = await api.request('/kindle-research/generate-book-ideas', {
        method: 'POST',
        body: JSON.stringify(ideaRequest)
      });
      setGeneratedIdeas(response.ideas);
      await loadAllData();
      trackFeatureUse('ai_ideas_generated');
    } catch (err) {
      setError('Failed to generate ideas: ' + err.message);
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const handleGenerateOutline = async (ideaId) => {
    // Check access
    const access = await checkFeatureAccess('ai_outlines');
    if (!access.allowed) {
      setUpgradeMessage(access.message || 'AI Outline Builder is a premium feature. Upgrade to create detailed book outlines!');
      setShowUpgradeModal(true);
      return;
    }
    
    setGeneratingOutline(true);
    try {
      const response = await api.request('/kindle-research/generate-outline', {
        method: 'POST',
        body: JSON.stringify({ book_idea_id: ideaId, chapter_count: 20, include_beats: true })
      });
      setGeneratedOutline(response.outline);
      await loadAllData();
      trackFeatureUse('ai_outline_generated');
    } catch (err) {
      setError('Failed to generate outline: ' + err.message);
    } finally {
      setGeneratingOutline(false);
    }
  };

  const handleDeleteNiche = async (nicheId) => {
    if (!window.confirm('Delete this niche and all its books?')) return;
    try {
      await api.request(`/kindle-research/niches/${nicheId}`, { method: 'DELETE' });
      await loadAllData();
      if (selectedNiche?.id === nicheId) {
        setSelectedNiche(null);
        setBooks([]);
      }
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Delete this book?')) return;
    try {
      await api.request(`/kindle-research/books/${bookId}`, { method: 'DELETE' });
      await loadNicheBooks(selectedNiche.id);
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  // Variant-specific UI helpers
  const getVariantBadge = () => {
    if (!abVariant) return null;
    
    const badges = {
      'A': { color: 'bg-gray-100 text-gray-700', text: 'Preview Mode' },
      'B': { color: 'bg-blue-100 text-blue-700', text: 'Free Tools' },
      'C': { color: 'bg-green-100 text-green-700', text: 'Limited Trial' },
      'D': { color: 'bg-purple-100 text-purple-700', text: `Trial: ${abVariant.trial_days_remaining || 7} days left` }
    };
    
    const badge = badges[abVariant.variant];
    if (!badge) return null;
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </Layout>
    );
  }

  // Check if trial expired for variant D
  const isTrialExpired = abVariant?.variant === 'D' && abVariant?.trial_expired;

  // Shared props for PaywallHero across all variants
  const paywallSharedProps = {
    paypalClientId,
    onPurchaseSetup: handlePurchaseSetup,
    onPurchaseSubscription: handlePurchaseSubscription,
    subscriptionStatus,
    processing,
    error,
    successMessage
  };

  // VARIANT A: Strict Paywall
  if (!subscriptionStatus?.has_access && abVariant?.variant === 'A') {
    return <KindleVariantA PaywallHero={PaywallHero} TutorialPreview={TutorialPreview} navigate={navigate} sharedProps={paywallSharedProps} />;
  }

  // VARIANT B: BSR Teaser
  if (!subscriptionStatus?.has_access && abVariant?.variant === 'B') {
    return <KindleVariantB
      PaywallHero={PaywallHero} getVariantBadge={getVariantBadge} sharedProps={paywallSharedProps}
      bsrInput={bsrInput} setBsrInput={setBsrInput} bsrResult={bsrResult} bsrRemaining={bsrRemaining}
      handleCalculateBsr={handleCalculateBsr} categories={categories}
    />;
  }

  // VARIANT C: Limited Trial
  if (!subscriptionStatus?.has_access && abVariant?.variant === 'C') {
    return <KindleVariantC
      LimitedDashboard={LimitedDashboard} NicheModal={NicheModal} BookModal={BookModal}
      UpgradeModal={UpgradeModal} getVariantBadge={getVariantBadge} sharedProps={paywallSharedProps}
      niches={niches} selectedNiche={selectedNiche} books={books} categories={categories}
      loadNicheBooks={loadNicheBooks} bsrInput={bsrInput} setBsrInput={setBsrInput} bsrResult={bsrResult}
      handleCalculateBsr={handleCalculateBsr} handleCreateNiche={handleCreateNiche}
      handleAddBook={handleAddBook} handleDeleteNiche={handleDeleteNiche} handleDeleteBook={handleDeleteBook}
      handlePurchaseSetup={handlePurchaseSetup} showNicheModal={showNicheModal} setShowNicheModal={setShowNicheModal}
      showBookModal={showBookModal} setShowBookModal={setShowBookModal} nicheForm={nicheForm}
      setNicheForm={setNicheForm} bookForm={bookForm} setBookForm={setBookForm} processing={processing}
      showUpgradeModal={showUpgradeModal} setShowUpgradeModal={setShowUpgradeModal}
      upgradeMessage={upgradeMessage} paypalClientId={paypalClientId}
    />;
  }

  // VARIANT D: Full Trial - Trial Expired
  if (!subscriptionStatus?.has_access && abVariant?.variant === 'D') {
    if (isTrialExpired) {
      return <KindleVariantDExpired PaywallHero={PaywallHero} sharedProps={paywallSharedProps} />;
    }
    // Active trial - fall through to main dashboard
  }

  // FULL ACCESS DASHBOARD (Paid users OR Variant D with active trial)
  const hasFullAccess = subscriptionStatus?.has_access || (abVariant?.variant === 'D' && !isTrialExpired);
  
  if (!hasFullAccess) {
    // Fallback to paywall
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <div className="container mx-auto px-4 py-12">
            <PaywallHero paypalClientId={paypalClientId} 
              variant="default"
              onPurchaseSetup={handlePurchaseSetup}
              onPurchaseSubscription={handlePurchaseSubscription}
              subscriptionStatus={subscriptionStatus}
              processing={processing}
              error={error}
              successMessage={successMessage}
            />
          </div>
        </div>
      </Layout>
    );
  }

  // Main Full Dashboard
  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-purple-600" />
                Kindle Research Pro
              </h1>
              <p className="text-gray-600">Find profitable niches and dominate Amazon Kindle</p>
            </div>
            <div className="flex items-center gap-3">
              {abVariant?.variant === 'D' && !subscriptionStatus?.has_access && (
                <div className="flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full">
                  <Clock className="w-4 h-4" />
                  <span className="font-semibold">{abVariant.trial_days_remaining || 7} days left in trial</span>
                  <Button size="sm" onClick={handlePurchaseSetup} className="ml-2">
                    Subscribe
                  </Button>
                </div>
              )}
              <Button 
                variant="outline" 
                onClick={() => setShowBsrCalculator(true)}
                data-testid="bsr-calculator-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                BSR Calculator
              </Button>
              <Button 
                onClick={() => setShowNicheModal(true)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="new-niche-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Niche
              </Button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
              <button onClick={() => setError('')} className="ml-auto">×</button>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              {successMessage}
              <button onClick={() => setSuccessMessage('')} className="ml-auto">×</button>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="dashboard">📊 Dashboard</TabsTrigger>
              <TabsTrigger value="niches">📂 My Niches</TabsTrigger>
              <TabsTrigger value="ideas">💡 Book Ideas</TabsTrigger>
              <TabsTrigger value="outlines">📝 Outlines</TabsTrigger>
              <TabsTrigger value="tutorial">🎓 Tutorial</TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard">
              <FullDashboard 
                niches={niches}
                ideas={ideas}
                selectedNiche={selectedNiche}
                books={books}
                onSelectNiche={loadNicheBooks}
                onCreateNiche={() => setShowNicheModal(true)}
                onGenerateIdeas={() => setShowIdeaGenerator(true)}
              />
            </TabsContent>

            <TabsContent value="niches">
              <NichesTab 
                niches={niches}
                selectedNiche={selectedNiche}
                books={books}
                categories={categories}
                onSelectNiche={loadNicheBooks}
                onCreateNiche={() => setShowNicheModal(true)}
                onAddBook={() => setShowBookModal(true)}
                onDeleteNiche={handleDeleteNiche}
                onDeleteBook={handleDeleteBook}
              />
            </TabsContent>

            <TabsContent value="ideas">
              <IdeasTab 
                ideas={ideas}
                niches={niches}
                generatingOutline={generatingOutline}
                onGenerateIdeas={() => setShowIdeaGenerator(true)}
                onGenerateOutline={handleGenerateOutline}
              />
            </TabsContent>

            <TabsContent value="outlines">
              <OutlinesTab outlines={outlines} />
            </TabsContent>

            <TabsContent value="tutorial">
              <TutorialTab navigate={navigate} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Modals */}
        <NicheModal 
          open={showNicheModal}
          onClose={() => setShowNicheModal(false)}
          form={nicheForm}
          setForm={setNicheForm}
          categories={categories}
          onSubmit={handleCreateNiche}
          processing={processing}
        />
        <BookModal
          open={showBookModal}
          onClose={() => setShowBookModal(false)}
          form={bookForm}
          setForm={setBookForm}
          nicheName={selectedNiche?.name}
          onSubmit={handleAddBook}
          processing={processing}
        />
        <BsrCalculatorModal
          open={showBsrCalculator}
          onClose={() => setShowBsrCalculator(false)}
          bsrInput={bsrInput}
          setBsrInput={setBsrInput}
          bsrResult={bsrResult}
          onCalculate={handleCalculateBsr}
        />
        <IdeaGeneratorModal
          open={showIdeaGenerator}
          onClose={() => setShowIdeaGenerator(false)}
          niches={niches}
          ideaRequest={ideaRequest}
          setIdeaRequest={setIdeaRequest}
          generatingIdeas={generatingIdeas}
          generatedIdeas={generatedIdeas}
          onGenerate={handleGenerateIdeas}
        />
        <UpgradeModal paypalClientId={paypalClientId}
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          message={upgradeMessage}
          onPurchase={handlePurchaseSetup}
          processing={processing}
        />
      </div>
    </Layout>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

function PaywallHero({ variant, compact, onPurchaseSetup, onPurchaseSubscription, subscriptionStatus, processing, error, successMessage, paypalClientId }) {
  const [payMethod, setPayMethod] = useState('stripe');
  return (
    <div className={compact ? "" : "text-center mb-12"}>
      {!compact && (
        <>
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Amazon Bestseller Research System
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Kindle Research Pro
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Find profitable niches, validate book ideas, and dominate Amazon Kindle.
          </p>
        </>
      )}

      <Card className={`border-2 border-purple-300 shadow-xl ${compact ? '' : 'max-w-2xl mx-auto'}`}>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={compact ? "text-lg" : "text-2xl"}>Kindle Research Pro</CardTitle>
              <CardDescription className="text-purple-100">
                Complete research toolkit
              </CardDescription>
            </div>
            <div className="text-right">
              <div className={compact ? "text-2xl font-bold" : "text-3xl font-bold"}>$295</div>
              <div className="text-sm text-purple-100">one-time setup</div>
              <div className="text-lg font-semibold mt-1">+ $99/mo</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
              <CheckCircle className="w-4 h-4" />
              Save Thousands vs. $1,995 courses
            </div>
          </div>

          {!compact && (
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <div className="flex items-center gap-2"><Search className="w-4 h-4 text-purple-600" /> Niche Research</div>
              <div className="flex items-center gap-2"><Calculator className="w-4 h-4 text-purple-600" /> BSR Calculator</div>
              <div className="flex items-center gap-2"><Lightbulb className="w-4 h-4 text-purple-600" /> AI Ideas</div>
              <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-purple-600" /> Outlines</div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg mb-3 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
          {successMessage && (
            <div className="bg-green-50 text-green-700 px-3 py-2 rounded-lg mb-3 text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />{successMessage}
            </div>
          )}

          {!subscriptionStatus?.setup_paid ? (
            <div className="space-y-3">
              <div className="flex gap-2 mb-2">
                <button onClick={() => setPayMethod('stripe')} className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm rounded-lg border ${payMethod === 'stripe' ? 'bg-purple-100 border-purple-400 text-purple-800 font-semibold' : 'bg-white border-gray-200 text-gray-600'}`}>
                  <CreditCard className="w-4 h-4" /> Card
                </button>
                <button onClick={() => setPayMethod('paypal')} className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm rounded-lg border ${payMethod === 'paypal' ? 'bg-blue-100 border-blue-400 text-blue-800 font-semibold' : 'bg-white border-gray-200 text-gray-600'}`}>
                  PayPal
                </button>
              </div>
              {payMethod === 'stripe' && (
                <Button 
                  onClick={onPurchaseSetup}
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                  data-testid="purchase-setup-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Get Started - $295
                </Button>
              )}
              {payMethod === 'paypal' && paypalClientId && (
                <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD' }}>
                  <PayPalButtons
                    style={{ layout: 'horizontal', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
                    disabled={processing}
                    createOrder={async () => {
                      const resp = await fetch(`${API_URL}/api/paypal/create-order`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                        body: JSON.stringify({ product: 'kindle', package_id: 'setup', host_url: window.location.origin })
                      });
                      const data = await resp.json();
                      return data.order_id;
                    }}
                    onApprove={async (data) => {
                      const resp = await fetch(`${API_URL}/api/paypal/capture-order`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                        body: JSON.stringify({ order_id: data.orderID, product: 'kindle', package_id: 'setup' })
                      });
                      const result = await resp.json();
                      if (result.status === 'completed') {
                        window.location.href = '/kindle-research?payment=success&method=paypal';
                      }
                    }}
                  />
                </PayPalScriptProvider>
              )}
            </div>
          ) : (
            <>
              <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg text-center font-semibold text-sm mb-3">
                ✓ Setup Fee Paid
              </div>
              <Button 
                onClick={onPurchaseSubscription}
                disabled={processing}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Activate - $99/mo
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TutorialPreview() {
  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-start">
        <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">1</div>
        <div>
          <h4 className="font-semibold">Browse Amazon Categories</h4>
          <p className="text-gray-600 text-sm">Find niches with BSR 5,000-50,000 in top books.</p>
        </div>
      </div>
      <div className="flex gap-4 items-start">
        <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">2</div>
        <div>
          <h4 className="font-semibold">Track Top Books</h4>
          <p className="text-gray-600 text-sm">Record BSR, reviews, price, and tropes.</p>
        </div>
      </div>
      <div className="flex gap-4 items-start">
        <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">3</div>
        <div>
          <h4 className="font-semibold">Analyze & Generate Ideas</h4>
          <p className="text-gray-600 text-sm">Use AI to create winning book concepts.</p>
        </div>
      </div>
    </div>
  );
}

function LimitedDashboard({ niches, selectedNiche, books, categories, onSelectNiche, onCreateNiche, onAddBook, onDeleteNiche, onDeleteBook, maxNiches, maxBooks, onUpgrade }) {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">My Niches ({niches.length}/{maxNiches})</h3>
          <Button size="sm" onClick={onCreateNiche} disabled={niches.length >= maxNiches}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {niches.map(niche => (
          <Card 
            key={niche.id}
            className={`cursor-pointer ${selectedNiche?.id === niche.id ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => onSelectNiche(niche.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{niche.name}</div>
                  <div className="text-sm text-gray-600">{niche.category}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onDeleteNiche(niche.id); }} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
        {niches.length === 0 && (
          <div className="text-center py-8 text-gray-500">Create your first niche!</div>
        )}
      </div>

      <div className="md:col-span-2">
        {selectedNiche ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedNiche.name}</CardTitle>
                  <CardDescription>{selectedNiche.category}</CardDescription>
                </div>
                <Button onClick={onAddBook} disabled={books.length >= maxBooks}>
                  <Plus className="w-4 h-4 mr-2" /> Add Book ({books.length}/{maxBooks})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {books.map(book => (
                  <div key={book.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{book.title}</div>
                      <div className="text-sm text-gray-600">{book.author}</div>
                    </div>
                    <button onClick={() => onDeleteBook(book.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {books.length === 0 && <div className="text-center py-4 text-gray-500">Add books to track</div>}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              Select or create a niche to start tracking books
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function FullDashboard({ niches, ideas, selectedNiche, books, onSelectNiche, onCreateNiche, onGenerateIdeas }) {
  return (
    <>
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{niches.length}</div>
                <div className="text-gray-600">Niches Tracked</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">
                  {niches.reduce((sum, n) => sum + (n.book_count || 0), 0)}
                </div>
                <div className="text-gray-600">Books Analyzed</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-3xl font-bold">{ideas.length}</div>
                <div className="text-gray-600">Ideas Generated</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-purple-600" /> Start Research
            </h3>
            <p className="text-gray-600 mb-4">Create a new niche to track bestselling books.</p>
            <Button onClick={onCreateNiche} className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Create New Niche
            </Button>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" /> Generate Ideas
            </h3>
            <p className="text-gray-600 mb-4">Use AI to generate book ideas from your research.</p>
            <Button onClick={onGenerateIdeas} className="w-full" disabled={niches.length === 0}>
              <Lightbulb className="w-4 h-4 mr-2" /> Generate Book Ideas
            </Button>
          </CardContent>
        </Card>
      </div>

      {niches.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-bold mb-4">Recent Niches</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {niches.slice(0, 3).map(niche => (
              <Card key={niche.id} className="cursor-pointer hover:shadow-lg" onClick={() => onSelectNiche(niche.id)}>
                <CardContent className="p-4">
                  <div className="font-semibold truncate">{niche.name}</div>
                  <div className="text-sm text-gray-600">{niche.category}</div>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-purple-600">{niche.book_count || 0} books</span>
                    {niche.avg_bsr && <span>Avg BSR: {niche.avg_bsr.toLocaleString()}</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function NichesTab({ niches, selectedNiche, books, categories, onSelectNiche, onCreateNiche, onAddBook, onDeleteNiche, onDeleteBook }) {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">My Niches</h3>
          <Button size="sm" onClick={onCreateNiche}><Plus className="w-4 h-4" /></Button>
        </div>
        {niches.map(niche => (
          <Card key={niche.id} className={`cursor-pointer ${selectedNiche?.id === niche.id ? 'ring-2 ring-purple-500' : ''}`} onClick={() => onSelectNiche(niche.id)}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{niche.name}</div>
                  <div className="text-sm text-gray-600">{niche.category}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onDeleteNiche(niche.id); }} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span>{niche.book_count || 0} books</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {niches.length === 0 && <div className="text-center py-8 text-gray-500">No niches yet</div>}
      </div>

      <div className="md:col-span-2">
        {selectedNiche ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedNiche.name}</CardTitle>
                  <CardDescription>{selectedNiche.category}</CardDescription>
                </div>
                <Button onClick={onAddBook}><Plus className="w-4 h-4 mr-2" /> Add Book</Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedNiche.top_tropes?.length > 0 && (
                <div className="mb-6">
                  <div className="font-semibold mb-2">Top Tropes</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedNiche.top_tropes.map(trope => (
                      <span key={trope} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">{trope}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <div className="font-semibold">Tracked Books ({books.length})</div>
                {books.map(book => (
                  <div key={book.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{book.title}</div>
                      <div className="text-sm text-gray-600">{book.author}</div>
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        {book.bsr && <span>BSR: {book.bsr.toLocaleString()}</span>}
                        {book.review_count && <span>{book.review_count} reviews</span>}
                        {book.price && <span>${book.price}</span>}
                      </div>
                    </div>
                    <button onClick={() => onDeleteBook(book.id)} className="text-red-500 p-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {books.length === 0 && <div className="text-center py-8 text-gray-500">Add books to track</div>}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card><CardContent className="p-12 text-center text-gray-500">Select a niche to view details</CardContent></Card>
        )}
      </div>
    </div>
  );
}

function IdeasTab({ ideas, niches, generatingOutline, onGenerateIdeas, onGenerateOutline }) {
  return (
    <>
      <div className="mb-6">
        <Button onClick={onGenerateIdeas} disabled={niches.length === 0}>
          <Sparkles className="w-4 h-4 mr-2" /> Generate New Ideas
        </Button>
        {niches.length === 0 && <span className="ml-3 text-sm text-gray-500">Create a niche first</span>}
      </div>
      {ideas.length > 0 ? (
        <div className="space-y-4">
          {ideas.map(idea => (
            <Card key={idea.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-500">Generated {new Date(idea.created_at).toLocaleDateString()}</div>
                  <Button size="sm" onClick={() => onGenerateOutline(idea.id)} disabled={generatingOutline}>
                    {generatingOutline ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Generate Outline
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{idea.ideas_text}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="p-12 text-center text-gray-500">No ideas generated yet</CardContent></Card>
      )}
    </>
  );
}

function OutlinesTab({ outlines }) {
  return outlines.length > 0 ? (
    <div className="space-y-4">
      {outlines.map(outline => (
        <Card key={outline.id}>
          <CardContent className="p-6">
            <div className="text-sm text-gray-500 mb-4">Generated {new Date(outline.created_at).toLocaleDateString()}</div>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap">{outline.outline_text}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  ) : (
    <Card><CardContent className="p-12 text-center text-gray-500">No outlines generated yet</CardContent></Card>
  );
}

function TutorialTab({ navigate }) {
  return (
    <div className="max-w-4xl">
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>🎓 How to Find Profitable Kindle Niches</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <TutorialPreview />
          <div className="flex gap-4 items-start">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">4</div>
            <div>
              <h4 className="font-semibold">Generate & Validate Ideas</h4>
              <p className="text-gray-600 text-sm">Use AI to create book concepts based on your research.</p>
            </div>
          </div>
          <div className="flex gap-4 items-start">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shrink-0">5</div>
            <div>
              <h4 className="font-semibold">Create Outline & Write</h4>
              <p className="text-gray-600 text-sm">Generate chapter outlines and start writing your bestseller!</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="text-4xl">📚</div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">KDP Account Setup Guide</h3>
              <p className="text-gray-600">Business and tax requirements for Amazon KDP</p>
            </div>
            <Button onClick={() => navigate('/kdp-setup-guide')}>
              View Guide <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Modal Components
function NicheModal({ open, onClose, form, setForm, categories, onSubmit, processing }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create New Niche</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Niche Name *</label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Cozy Mystery" data-testid="niche-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category *</label>
            <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {Object.keys(categories).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subcategory</label>
            <Select value={form.subcategory} onValueChange={v => setForm({...form, subcategory: v})}>
              <SelectTrigger><SelectValue placeholder="Select subcategory" /></SelectTrigger>
              <SelectContent>
                {(categories[form.category] || []).map(sub => <SelectItem key={sub} value={sub}>{sub}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Competition Level</label>
            <Select value={form.competition_level} onValueChange={v => setForm({...form, competition_level: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onSubmit} disabled={!form.name || !form.category || processing} className="w-full" data-testid="create-niche-btn">
            {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Niche
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookModal({ open, onClose, form, setForm, nicheName, onSubmit, processing }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add Book to {nicheName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Title *</label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Book title" data-testid="book-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Author</label>
            <Input value={form.author} onChange={e => setForm({...form, author: e.target.value})} placeholder="Author name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">BSR</label>
            <Input type="number" value={form.bsr} onChange={e => setForm({...form, bsr: e.target.value})} placeholder="12345" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Price ($)</label>
            <Input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="4.99" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reviews</label>
            <Input type="number" value={form.review_count} onChange={e => setForm({...form, review_count: e.target.value})} placeholder="1234" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Tropes (comma-separated)</label>
            <Input value={form.tropes} onChange={e => setForm({...form, tropes: e.target.value})} placeholder="Enemies to Lovers, Second Chance" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Cover Style</label>
            <Input value={form.cover_style} onChange={e => setForm({...form, cover_style: e.target.value})} placeholder="Dark background, illustrated" />
          </div>
          <div className="col-span-2">
            <Button onClick={onSubmit} disabled={!form.title || processing} className="w-full" data-testid="add-book-submit-btn">
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add Book
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BsrCalculatorModal({ open, onClose, bsrInput, setBsrInput, bsrResult, onCalculate }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" /> BSR Sales Calculator</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Best Seller Rank (BSR)</label>
            <div className="flex gap-2">
              <Input type="number" value={bsrInput} onChange={e => setBsrInput(e.target.value)} placeholder="Enter BSR" data-testid="bsr-input" />
              <Button onClick={onCalculate} data-testid="calculate-bsr-btn">Calculate</Button>
            </div>
          </div>
          {bsrResult && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="text-center">
                <div className="text-sm text-gray-600">BSR #{bsrResult.bsr.toLocaleString()}</div>
                <div className="text-xl font-bold text-purple-600">{bsrResult.interpretation}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-lg font-bold">{bsrResult.estimated_daily_sales}</div>
                  <div className="text-xs text-gray-600">Daily Sales</div>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-lg font-bold">{bsrResult.estimated_monthly_sales}</div>
                  <div className="text-xs text-gray-600">Monthly</div>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-lg font-bold">${bsrResult.revenue_estimates.monthly}</div>
                  <div className="text-xs text-gray-600">Monthly Rev</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IdeaGeneratorModal({ open, onClose, niches, ideaRequest, setIdeaRequest, generatingIdeas, generatedIdeas, onGenerate }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI Book Idea Generator</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Select Niche *</label>
            <Select value={ideaRequest.niche_id} onValueChange={v => setIdeaRequest({...ideaRequest, niche_id: v})}>
              <SelectTrigger data-testid="idea-niche-select"><SelectValue placeholder="Choose a niche" /></SelectTrigger>
              <SelectContent>
                {niches.map(n => <SelectItem key={n.id} value={n.id}>{n.name} ({n.category})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Style Preference</label>
            <Select value={ideaRequest.style_preference} onValueChange={v => setIdeaRequest({...ideaRequest, style_preference: v})}>
              <SelectTrigger><SelectValue placeholder="Any style" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="similar">Similar to Top Sellers</SelectItem>
                <SelectItem value="unique">Unique Twist</SelectItem>
                <SelectItem value="trending">Trending Elements</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Target Audience</label>
            <Input value={ideaRequest.target_audience} onChange={e => setIdeaRequest({...ideaRequest, target_audience: e.target.value})} placeholder="e.g., Women 25-45" />
          </div>
          <Button onClick={onGenerate} disabled={!ideaRequest.niche_id || generatingIdeas} className="w-full" data-testid="generate-ideas-btn">
            {generatingIdeas ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate 5 Book Ideas</>}
          </Button>
          {generatedIdeas && (
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <div className="font-semibold mb-3">Generated Ideas:</div>
              <div className="prose prose-sm whitespace-pre-wrap">{generatedIdeas}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeModal({ open, onClose, message, onPurchase, processing, paypalClientId }) {
  const [payMethod, setPayMethod] = useState('stripe');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-purple-600" /> Upgrade Required</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-gray-600">{message}</p>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="font-bold text-purple-900">Kindle Research Pro</div>
            <div className="text-purple-700">$295 setup + $99/month</div>
            <ul className="mt-2 text-sm text-purple-800 space-y-1">
              <li>✓ Unlimited niches & books</li>
              <li>✓ Unlimited BSR calculations</li>
              <li>✓ AI book idea generator</li>
              <li>✓ AI outline builder</li>
            </ul>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPayMethod('stripe')} className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm rounded-lg border ${payMethod === 'stripe' ? 'bg-purple-100 border-purple-400 text-purple-800 font-semibold' : 'bg-white border-gray-200 text-gray-600'}`}>
              <CreditCard className="w-4 h-4" /> Card
            </button>
            <button onClick={() => setPayMethod('paypal')} className={`flex-1 flex items-center justify-center gap-1 py-2 text-sm rounded-lg border ${payMethod === 'paypal' ? 'bg-blue-100 border-blue-400 text-blue-800 font-semibold' : 'bg-white border-gray-200 text-gray-600'}`}>
              PayPal
            </button>
          </div>
          {payMethod === 'stripe' && (
            <Button onClick={onPurchase} disabled={processing} className="w-full bg-purple-600 hover:bg-purple-700">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Upgrade Now
            </Button>
          )}
          {payMethod === 'paypal' && paypalClientId && (
            <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD' }}>
              <PayPalButtons
                style={{ layout: 'horizontal', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
                createOrder={async () => {
                  const resp = await fetch(`${API_URL}/api/paypal/create-order`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ product: 'kindle', package_id: 'setup', host_url: window.location.origin })
                  });
                  const data = await resp.json();
                  return data.order_id;
                }}
                onApprove={async (data) => {
                  const resp = await fetch(`${API_URL}/api/paypal/capture-order`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ order_id: data.orderID, product: 'kindle', package_id: 'setup' })
                  });
                  const result = await resp.json();
                  if (result.status === 'completed') {
                    window.location.href = '/kindle-research?payment=success&method=paypal';
                  }
                }}
              />
            </PayPalScriptProvider>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KindleResearch;
